if (!customElements.get('td-shipping-bar')) {
    class TDShippingBar extends HTMLElement {
        constructor() {
            super();
            this.listenerAbortController = null;
            this.requestAbortController = null;
            this.PROGRESS_BAR_CLASS = '.td-shipping-bar__bar-progress';
            this.PROGRESS_MESSAGE_CLASS = '.td-shipping-bar__message';
            this.PROGRESS_CONTAINER_CLASS = '.td-shipping-bar__container';

            this.progressBar = this.querySelector(this.PROGRESS_BAR_CLASS);
            this.progressBarContainer = this.querySelector(this.PROGRESS_CONTAINER_CLASS);
            this.progressMessage = this.querySelector(this.PROGRESS_MESSAGE_CLASS);
        }

        connectedCallback() {
            if (this.listenerAbortController) {
                this.listenerAbortController.abort();
            }

            this.listenerAbortController = new AbortController();
            const listenerOptions = { signal: this.listenerAbortController.signal };

            document.addEventListener('ajaxProduct:added', () => {
                this.cartTotal();
            }, listenerOptions);

            document.addEventListener('cart:updated', (event) => {
                const cartTotal = event.detail && event.detail.cart
                    ? event.detail.cart.total_price
                    : undefined;

                if (this.isValidCartTotal(cartTotal)) {
                    this.abortCartRequest();
                    this.updateUI(Number(cartTotal));
                    return;
                }

                this.cartTotal();
            }, listenerOptions);
        }

        disconnectedCallback() {
            if (this.listenerAbortController) {
                this.listenerAbortController.abort();
                this.listenerAbortController = null;
            }

            this.abortCartRequest();
        }

        isValidCartTotal(cartTotal) {
            return cartTotal !== null
                && cartTotal !== ''
                && cartTotal !== undefined
                && Number.isFinite(Number(cartTotal));
        }

        formatMoney(cents) {
            const currency = window.theme && window.theme.Currency;
            const moneyFormat = window.theme && window.theme.settings
                ? window.theme.settings.moneyFormat
                : undefined;

            if (currency && typeof currency.formatMoney === 'function' && moneyFormat) {
                return currency.formatMoney(cents, moneyFormat);
            }

            const currencyCode = this.progressBarContainer.dataset.currencyCode || 'USD';
            const locale = document.documentElement.lang || undefined;

            try {
                return new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: currencyCode
                }).format(cents / 100);
            } catch (error) {
                return `${currencyCode} ${(cents / 100).toFixed(2)}`;
            }
        }

        updateUI(cartTotalCents) {
            const minimumCents = Number(
                this.progressBarContainer && this.progressBarContainer.dataset.shippingMinCents
            );
            const cartTotal = Number(cartTotalCents);

            if (
                !this.progressBar
                || !this.progressBarContainer
                || !this.progressMessage
                || !Number.isFinite(minimumCents)
                || minimumCents <= 0
                || !Number.isFinite(cartTotal)
            ) {
                return;
            }

            const safeMinimumCents = Math.round(minimumCents);
            const safeCartTotalCents = Math.max(0, Math.round(cartTotal));
            const progressPercent = Math.min(
                100,
                Math.max(0, (safeCartTotalCents / safeMinimumCents) * 100)
            );
            const remainingCents = Math.max(0, safeMinimumCents - safeCartTotalCents);
            const completeMessage = this.progressBarContainer.dataset.completeMessage;
            const remainingMessage = this.progressBarContainer.dataset.remainingMessage;

            this.progressBar.style.width = `${progressPercent}%`;

            if (progressPercent >= 100) {
                if (completeMessage) {
                    this.progressMessage.innerHTML = completeMessage;
                }
                return;
            }

            if (remainingMessage) {
                const remainingMoney = this.formatMoney(remainingCents);
                this.progressMessage.innerHTML = remainingMessage.split('[amount]').join(remainingMoney);
            }
        }

        getCartUrl() {
            if (window.theme && window.theme.routes && window.theme.routes.cart) {
                return window.theme.routes.cart;
            }

            const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
                ? window.Shopify.routes.root
                : '/';
            const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

            return `${normalizedRoot}cart.js`;
        }

        abortCartRequest() {
            if (this.requestAbortController) {
                this.requestAbortController.abort();
                this.requestAbortController = null;
            }
        }

        async cartTotal() {
            this.abortCartRequest();

            const requestController = new AbortController();
            this.requestAbortController = requestController;

            try {
                const response = await fetch(this.getCartUrl(), {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json'
                    },
                    credentials: 'same-origin',
                    signal: requestController.signal
                });

                if (!response.ok) {
                    return;
                }

                const cart = await response.json();

                if (this.isValidCartTotal(cart.total_price)) {
                    this.updateUI(Number(cart.total_price));
                }
            } catch (error) {
                // An aborted or failed request leaves the last known cart state in place.
            } finally {
                if (this.requestAbortController === requestController) {
                    this.requestAbortController = null;
                }
            }
        }
    }

    customElements.define('td-shipping-bar', TDShippingBar);
}
